import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import {
  ArrowLeft, Users, Mail, TrendingUp, FileText,
  BarChart3, DollarSign, Send, Eye, Edit3,
  ShieldAlert, Plus, Save, X, CheckCircle, Clock, XCircle,
  AlertTriangle, Flag
} from 'lucide-react';

const STATUS_STYLES = {
  draft:          'bg-gray-800 border-gray-700 text-gray-300',
  pending_review: 'bg-yellow-900/30 border-yellow-700/40 text-yellow-400',
  approved:       'bg-green-900/30 border-green-700/40 text-green-400',
  rejected:       'bg-red-900/30 border-red-700/40 text-red-400',
  sent:           'bg-blue-900/30 border-blue-700/40 text-blue-400',
};

const REPORT_STATUS_STYLES = {
  pending:  'bg-yellow-900/30 border-yellow-700/40 text-yellow-400',
  resolved: 'bg-green-900/30 border-green-700/40 text-green-400',
  dismissed:'bg-gray-800 border-gray-700 text-gray-400',
};

const wardUrl = (councillor) => {
  if (!councillor) return '/';
  if (councillor.id === 'mayor' || councillor.ward === 'Mayor') return '/ward/mayor';
  return `/ward/ward-${councillor.ward.replace('Ward ', '')}`;
};

const AdminCouncillorView = () => {
  const { councillorId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [reports, setReports] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // Blast compose
  const [showBlastForm, setShowBlastForm] = useState(false);
  const [blastForm, setBlastForm] = useState({ subject: '', body: '', track_metrics: false });
  const [blastSaving, setBlastSaving] = useState(false);
  const [blastConfirm, setBlastConfirm] = useState(false);

  // Profile edit
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [main, reps] = await Promise.all([
        api.get(`/admin/councillors/${councillorId}/imitate`),
        api.get(`/admin/councillors/${councillorId}/reports`).catch(() => ({ data: { reports: [] } })),
      ]);
      setData(main.data);
      setReports(reps.data.reports || []);
      setProfileForm({
        name: main.data.councillor?.name || '',
        phone: main.data.councillor?.phone || '',
        bio: main.data.councillor?.bio || '',
        image_url: main.data.councillor?.image_url || '',
        email: main.data.councillor?.email || '',
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load councillor data');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  }, [councillorId, navigate]);

  useEffect(() => {
    if (!isAdmin()) { navigate('/admin'); return; }
    fetchData();
  }, [isAdmin, navigate, fetchData]);

  const handleCreateBlast = async () => {
    if (!blastForm.subject.trim() || !blastForm.body.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    setBlastSaving(true);
    try {
      await api.post(`/admin/councillors/${councillorId}/email-blasts`, blastForm);
      toast.success('Blast saved and approved. No emails sent until system is activated.');
      setShowBlastForm(false);
      setBlastConfirm(false);
      setBlastForm({ subject: '', body: '', track_metrics: false });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create blast');
    } finally {
      setBlastSaving(false);
    }
  };

  const handleProfileSave = async () => {
    setProfileSaving(true);
    try {
      await api.put(`/admin/councillors/${councillorId}/profile`, profileForm);
      toast.success('Profile updated');
      setEditingProfile(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C5A52A]"></div>
        </div>
      </Layout>
    );
  }
  if (!data) return null;

  const { councillor, posts, messages, subscribers, credits, email_blasts } = data;
  const isMayor = councillor.id === 'mayor' || councillor.ward === 'Mayor';
  const publicUrl = `${window.location.origin}${wardUrl(councillor)}`;
  const pendingReports = reports.filter(r => r.status === 'pending').length;

  return (
    <Layout>
      <div className="min-h-screen bg-black text-white">

        {/* Admin Banner */}
        <div className="bg-[#C5A52A] text-black px-4 py-2 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Eye className="w-4 h-4 flex-shrink-0" />
            <span className="font-mono text-xs font-bold uppercase tracking-wider">
              ADMIN VIEW — {councillor.name.toUpperCase()}{isMayor ? ' · MAYOR OF REGINA' : ` · ${councillor.ward.toUpperCase()}`}
            </span>
          </div>
          <button onClick={() => navigate('/admin')}
            className="flex items-center gap-1.5 bg-black/20 hover:bg-black/30 text-black font-mono text-xs px-3 py-1 transition-colors">
            <ArrowLeft className="w-3 h-3" />BACK TO ADMIN
          </button>
        </div>

        {/* Header */}
        <div className="border-b border-gray-800 bg-[#0a0a0a]">
          <div className="max-w-5xl mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <img
                  src={councillor.image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(councillor.name)}&background=1a1a1a&color=fff&size=128`}
                  alt={councillor.name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-gray-700"
                  onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(councillor.name)}&background=1a1a1a&color=fff&size=128`; }}
                />
                <div>
                  <h1 className="text-xl font-bold font-mono">
                    {isMayor ? 'MAYOR ' : 'COUNCILLOR '}
                    <span className="text-[#C5A52A]">{councillor.name.toUpperCase()}</span>
                  </h1>
                  <p className="text-gray-400 text-sm mt-0.5">
                    {isMayor ? 'City of Regina' : councillor.ward} &bull; {councillor.email}
                  </p>
                </div>
              </div>
              <a href={wardUrl(councillor)} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 px-4 py-2 text-sm transition-colors">
                <Eye className="w-4 h-4" />View Public Page
              </a>
            </div>

            {/* Tabs */}
            <div className="flex gap-6 mt-6 border-b border-gray-800 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'messages', label: `Messages (${messages.total})`, icon: Mail },
                { id: 'emails', label: `Email Blasts (${email_blasts.length})`, icon: Send },
                { id: 'reports', label: `Reports${pendingReports > 0 ? ` (${pendingReports})` : ''}`, icon: Flag },
                { id: 'profile', label: 'Profile', icon: FileText },
              ].map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 pb-3 border-b-2 transition-colors text-sm font-mono whitespace-nowrap ${
                    activeTab === tab.id ? 'border-[#C5A52A] text-[#C5A52A]' : 'border-transparent text-gray-400 hover:text-white'
                  }`}>
                  <tab.icon className="w-4 h-4" />{tab.label}
                  {tab.id === 'reports' && pendingReports > 0 && (
                    <span className="bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{pendingReports}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-5xl mx-auto px-4 py-8">

          {/* OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[{
                  label: 'Ward Posts', value: posts.total, sub: `${posts.issues} issues · ${posts.positive} positive`, icon: FileText, color: 'text-blue-400'
                }, {
                  label: 'Messages', value: messages.total, sub: `${messages.pending} pending · ${messages.replied} replied`, icon: Mail, color: 'text-yellow-400'
                }, {
                  label: 'Response Rate', value: `${messages.responsiveness_rate}%`,
                  sub: messages.responsiveness_rate >= 70 ? 'Excellent' : messages.responsiveness_rate >= 40 ? 'Good' : 'Needs Work',
                  icon: TrendingUp, color: messages.responsiveness_rate >= 70 ? 'text-green-500' : messages.responsiveness_rate >= 40 ? 'text-yellow-500' : 'text-red-500'
                }, {
                  label: 'Subscribers', value: subscribers, sub: 'Ward email list', icon: Users, color: 'text-purple-400'
                }].map((s) => (
                  <div key={s.label} className="bg-[#111] border border-[#1a1a1a] rounded p-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-mono text-xs text-gray-500 uppercase">{s.label}</p>
                      <s.icon className={`w-4 h-4 ${s.color}`} />
                    </div>
                    <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-xs text-gray-500 mt-1">{s.sub}</p>
                  </div>
                ))}
              </div>

              {credits && (
                <div className="bg-[#111] border border-[#C5A52A]/20 rounded p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-[#C5A52A]" />
                      <h3 className="font-mono text-sm text-[#C5A52A] uppercase tracking-wider">Email Credits</h3>
                    </div>
                    <span className="font-mono text-[10px] text-yellow-500 bg-yellow-900/20 border border-yellow-700/30 rounded px-3 py-1">
                      Stripe integration required to sell credits
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-6">
                    <div><p className="font-mono text-[10px] text-gray-500 uppercase mb-1">Free Remaining</p>
                      <p className="text-2xl font-bold text-green-400">{credits.free_remaining}</p>
                      <p className="text-xs text-gray-600">of {credits.free_credits_total} annual free</p></div>
                    <div><p className="font-mono text-[10px] text-gray-500 uppercase mb-1">Paid Credits</p>
                      <p className="text-2xl font-bold">{credits.paid_credits}</p></div>
                    <div><p className="font-mono text-[10px] text-gray-500 uppercase mb-1">Open Rate Tracking</p>
                      <p className={`font-mono text-sm font-bold ${credits.has_premium_tracking ? 'text-green-400' : 'text-gray-500'}`}>
                        {credits.has_premium_tracking ? 'ENABLED ✓' : 'NOT ENABLED'}</p></div>
                  </div>
                </div>
              )}

              <div className="bg-[#111] border border-[#1a1a1a] rounded p-6">
                <h3 className="font-mono text-sm text-gray-400 uppercase tracking-wider mb-3">Public Page URL</h3>
                <div className="flex gap-3">
                  <input readOnly value={publicUrl} className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] px-4 py-2 text-white font-mono text-sm" />
                  <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Copied!'); }}
                    className="bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-[#2a2a2a] px-4 py-2 text-sm transition-colors">Copy</button>
                  <a href={wardUrl(councillor)} target="_blank" rel="noopener noreferrer"
                    className="bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-bold px-4 py-2 text-sm transition-colors">View</a>
                </div>
              </div>
            </div>
          )}

          {/* MESSAGES */}
          {activeTab === 'messages' && (
            <div className="space-y-4">
              {/* Full legal notice */}
              <div className="bg-[#0d0d0d] border border-[#C5A52A]/40 rounded p-5">
                <div className="flex items-start gap-3 mb-3">
                  <ShieldAlert className="w-5 h-5 text-[#C5A52A] flex-shrink-0 mt-0.5" />
                  <p className="font-mono text-xs text-[#C5A52A] font-bold uppercase tracking-wider">
                    Notice Regarding Constituent Correspondence
                  </p>
                </div>
                <div className="text-xs text-gray-300 leading-relaxed space-y-3 font-mono">
                  <p>All constituent correspondence is sent and received directly through your official City of Regina email account. Message content is not accessible through your platform profile; however, the platform retains copies of all correspondence, both incoming and outgoing. While we do not intend to display or publish this content, we reserve the right to access and display it at any time, for any reason we deem necessary—particularly in situations where public discussion, dispute, or controversy directly involves the platform or its operations.</p>
                  <p><strong className="text-[#C5A52A]">Important:</strong> Ensure that <span className="text-white">AVoter@cs.cyqr.ca</span> is not filtered as spam, as all platform-generated correspondence will originate from this address. You may reply as normal. If you use auto-responders, you must configure an inbox rule to exclude this address. Auto-replies may negatively impact your responsiveness metrics, as outlined below. Do not change the subject line when replying to any correspondence.</p>
                  <p>For accountability and performance purposes, all responses are tracked, including response times and any non-responses. The results of these metrics may be displayed on your website and made available through your public profile. Response expectations are as follows: <strong className="text-white">You have 7 days from delivery to respond.</strong> A reminder will be issued after 7 days. After 14 days, a non-response will be recorded. Responses submitted after 14 days will receive a “Redemption,” which removes the non-response penalty but does not restore full responsiveness scoring.</p>
                  <p>Auto-replies or out-of-office emails falsely increase your correspondence total but do not get delivered to the user, thus increasing the amount but not the response factor, thereby lowering your overall score. We are working on a solution and will advise when implemented.</p>
                  <p>Safeguards have been implemented to reduce misuse of the platform; however, we cannot guarantee that these protections will not be bypassed. If you receive any communication that may be considered illegal or inappropriate (including threats), please report it immediately to <span className="text-white">councilhelp@cyqr.ca</span>. We maintain logs, including IP address information, and will provide such data if required by lawful demand or court order.</p>
                  <p>User messaging is rate-limited to prevent excessive contact. AI-based features are in development to assess response quality and identify repetitive or boilerplate replies. Notice will be provided once implemented.</p>
                  <p className="text-gray-500 border-t border-gray-800 pt-3">By accessing and/or using this platform, you agree to these user profile specific Terms of Service dated April 1, 2026, which are intentionally not outlined within the platform TOS for security purposes.</p>
                </div>
              </div>

              {/* Metrics */}
              <div className="bg-[#111] border border-[#1a1a1a] rounded p-6">
                <h3 className="font-mono text-sm text-gray-400 uppercase tracking-wider mb-4">Accountability Metrics</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  {[{ label: 'Total Received', value: messages.total, color: 'text-white' },
                    { label: 'Pending Reply', value: messages.pending, color: 'text-yellow-400' },
                    { label: 'Replied', value: messages.replied, color: 'text-green-400' }].map((m) => (
                    <div key={m.label}>
                      <p className={`text-3xl font-bold ${m.color}`}>{m.value}</p>
                      <p className="font-mono text-xs text-gray-500 mt-1 uppercase">{m.label}</p>
                    </div>
                  ))}
                </div>
                {messages.total > 0 && (
                  <div className="mt-6 bg-[#0a0a0a] rounded p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-xs text-gray-500 uppercase">Response Rate</span>
                      <span className={`font-mono text-sm font-bold ${messages.responsiveness_rate >= 70 ? 'text-green-400' : messages.responsiveness_rate >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {messages.responsiveness_rate}%
                      </span>
                    </div>
                    <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${messages.responsiveness_rate >= 70 ? 'bg-green-500' : messages.responsiveness_rate >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.min(messages.responsiveness_rate, 100)}%` }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* EMAIL BLASTS */}
          {activeTab === 'emails' && (
            <div className="space-y-4">
              {/* Feature overview */}
              <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-5">
                <h3 className="font-mono text-xs text-gray-400 uppercase tracking-wider mb-3">Email Blast System — What We Offer</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {[{ icon: CheckCircle, color: 'text-green-400', title: 'Draft & Submit', desc: 'Councillors write subject + body and submit for admin review.' },
                    { icon: CheckCircle, color: 'text-green-400', title: 'Admin Approval', desc: 'You review and approve or reject before any email reaches residents.' },
                    { icon: CheckCircle, color: 'text-green-400', title: 'Credit System', desc: '1 free blast per year included. Additional blasts require paid credits (Stripe).' },
                    { icon: Clock, color: 'text-yellow-400', title: 'Open Rate Tracking', desc: 'Premium add-on — tracks who opened the email. Requires Stripe purchase.' },
                    { icon: XCircle, color: 'text-gray-500', title: 'Unmonitored Replies', desc: 'Optionally allow residents to reply directly to councillor’s City email. Toggle per blast.' },
                    { icon: CheckCircle, color: 'text-blue-400', title: 'Admin Override', desc: 'You (admin) can create pre-approved blasts on behalf of any councillor from this panel.' }]
                    .map((f) => (
                    <div key={f.title} className="flex items-start gap-3">
                      <f.icon className={`w-4 h-4 ${f.color} flex-shrink-0 mt-0.5`} />
                      <div><p className="text-white font-semibold">{f.title}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{f.desc}</p></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <h2 className="font-mono text-sm text-gray-400 uppercase tracking-wider">
                  Blasts for {councillor.name} ({email_blasts.length})
                </h2>
                <div className="flex gap-2">
                  <button disabled
                    className="flex items-center gap-2 bg-gray-800 border border-gray-700 text-gray-500 font-mono text-xs px-4 py-2 cursor-not-allowed opacity-60"
                    title="Requires Stripe integration">
                    <DollarSign className="w-3.5 h-3.5" />ADD CREDITS (Stripe pending)
                  </button>
                  <button onClick={() => setShowBlastForm(true)}
                    className="flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-mono font-bold text-xs px-4 py-2 transition-colors">
                    <Plus className="w-3.5 h-3.5" />CREATE BLAST
                  </button>
                </div>
              </div>

              {/* Compose form */}
              {showBlastForm && (
                <div className="bg-[#111] border border-[#C5A52A]/30 rounded p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-mono text-sm text-[#C5A52A] uppercase tracking-wider">New Email Blast — {councillor.name}</h3>
                    <button onClick={() => { setShowBlastForm(false); setBlastConfirm(false); }} className="text-gray-500 hover:text-white">
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {!blastConfirm ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block font-mono text-xs text-gray-400 uppercase mb-1">Subject Line</label>
                        <input type="text" required value={blastForm.subject}
                          onChange={(e) => setBlastForm({ ...blastForm, subject: e.target.value })}
                          className="w-full bg-[#0a0a0a] border border-[#2a2a2a] px-4 py-3 text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                          placeholder="Monthly Ward Update — April 2026" />
                      </div>
                      <div>
                        <label className="block font-mono text-xs text-gray-400 uppercase mb-1">Email Body</label>
                        <textarea required rows={8} value={blastForm.body}
                          onChange={(e) => setBlastForm({ ...blastForm, body: e.target.value })}
                          className="w-full bg-[#0a0a0a] border border-[#2a2a2a] px-4 py-3 text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none resize-none"
                          placeholder="Dear Ward residents,..." />
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={blastForm.track_metrics}
                          onChange={(e) => setBlastForm({ ...blastForm, track_metrics: e.target.checked })}
                          className="accent-[#C5A52A]" />
                        <span className="font-mono text-xs text-gray-400">Enable open-rate tracking (premium)</span>
                      </label>
                      <div className="flex gap-3 justify-end">
                        <button type="button" onClick={() => setShowBlastForm(false)}
                          className="px-4 py-2 border border-gray-700 text-gray-400 hover:text-white font-mono text-xs transition-colors">
                          CANCEL
                        </button>
                        <button type="button"
                          onClick={() => {
                            if (!blastForm.subject.trim() || !blastForm.body.trim()) { toast.error('Subject and body required'); return; }
                            setBlastConfirm(true);
                          }}
                          className="flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-bold font-mono text-xs px-6 py-2 transition-colors">
                          REVIEW & CONFIRM →
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Confirmation step */
                    <div className="space-y-4">
                      <div className="bg-yellow-900/20 border border-yellow-700/40 rounded p-4 flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-mono text-xs text-yellow-400 font-bold uppercase mb-1">Confirm Before Saving</p>
                          <p className="text-sm text-gray-300">This blast will be saved as <strong>APPROVED</strong>. No emails will be sent until the email delivery system is activated via Stripe. You can review and modify it at any time before sending.</p>
                        </div>
                      </div>
                      <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-4 space-y-2">
                        <p className="font-mono text-xs text-gray-500 uppercase">Subject</p>
                        <p className="text-white font-semibold">{blastForm.subject}</p>
                        <p className="font-mono text-xs text-gray-500 uppercase mt-3">Body Preview</p>
                        <p className="text-gray-300 text-sm whitespace-pre-wrap line-clamp-6">{blastForm.body}</p>
                      </div>
                      <div className="flex gap-3 justify-end">
                        <button onClick={() => setBlastConfirm(false)}
                          className="px-4 py-2 border border-gray-700 text-gray-400 hover:text-white font-mono text-xs transition-colors">
                          ← EDIT
                        </button>
                        <button onClick={handleCreateBlast} disabled={blastSaving}
                          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold font-mono text-xs px-6 py-2 transition-colors">
                          <Save className="w-3.5 h-3.5" />
                          {blastSaving ? 'SAVING...' : 'YES, SAVE & APPROVE'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Blasts list */}
              {email_blasts.length === 0 && !showBlastForm ? (
                <div className="bg-[#111] border border-[#1a1a1a] rounded p-12 text-center">
                  <Send className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                  <p className="text-gray-500 font-mono text-sm mb-4">No email blasts yet for {councillor.name}.</p>
                  <button onClick={() => setShowBlastForm(true)}
                    className="bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-bold font-mono text-xs px-6 py-2">CREATE FIRST BLAST</button>
                </div>
              ) : (
                <div className="space-y-3">
                  {email_blasts.map((blast) => (
                    <div key={blast.id} className="bg-[#111] border border-[#1a1a1a] rounded p-5">
                      <div className="flex items-start justify-between mb-3 gap-3">
                        <div className="flex-1">
                          <p className="font-display text-white text-base">{blast.subject}</p>
                          <p className="font-mono text-[10px] text-gray-500 mt-0.5">
                            {new Date(blast.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                        <span className={`font-mono text-[10px] px-3 py-1 rounded border whitespace-nowrap ${STATUS_STYLES[blast.status] || STATUS_STYLES.draft}`}>
                          {blast.status.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed bg-[#0a0a0a] rounded p-3 border border-[#1a1a1a] whitespace-pre-wrap">{blast.body}</p>
                      {blast.admin_review_notes && (
                        <div className="mt-3 bg-yellow-900/10 border border-yellow-500/30 rounded p-3">
                          <p className="text-xs text-yellow-400"><strong>Note:</strong> {blast.admin_review_notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* REPORTS */}
          {activeTab === 'reports' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h2 className="font-mono text-sm text-gray-400 uppercase tracking-wider">Ward Post Reports — {councillor.ward}</h2>
                  <p className="text-xs text-gray-600 mt-1">Posts from your ward that residents have flagged for review.</p>
                </div>
                <div className="flex gap-3 font-mono text-xs">
                  <span className="text-yellow-400">{pendingReports} pending</span>
                  <span className="text-gray-500">{reports.length} total</span>
                </div>
              </div>

              {reports.length === 0 ? (
                <div className="bg-[#111] border border-[#1a1a1a] rounded p-12 text-center">
                  <Flag className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                  <p className="text-gray-500 font-mono text-sm">No reports filed for posts in {councillor.ward}.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {reports.map((report) => (
                    <div key={report.id} className={`bg-[#111] border rounded p-5 ${
                      report.status === 'pending' ? 'border-yellow-800/50' : 'border-[#1a1a1a]'
                    }`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1">
                          <p className="font-mono text-xs text-gray-500 uppercase mb-1">Reported Post</p>
                          <p className="text-white text-sm leading-relaxed line-clamp-3">
                            {report.posts?.content || '(Post content unavailable)'}
                          </p>
                          {report.posts?.user_name && (
                            <p className="text-gray-600 text-xs mt-1">by {report.posts.user_name}</p>
                          )}
                        </div>
                        <span className={`font-mono text-[10px] px-3 py-1 rounded border whitespace-nowrap ${REPORT_STATUS_STYLES[report.status] || REPORT_STATUS_STYLES.pending}`}>
                          {report.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="bg-[#0a0a0a] rounded p-3 border border-[#1a1a1a]">
                        <p className="font-mono text-[10px] text-gray-500 uppercase mb-1">Reason Filed</p>
                        <p className="text-gray-300 text-sm">{report.reason}</p>
                        <p className="font-mono text-[10px] text-gray-600 mt-2">
                          Reported by {report.reporter_name || 'Anonymous'} · {new Date(report.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* PROFILE */}
          {activeTab === 'profile' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-mono text-sm text-gray-400 uppercase tracking-wider">Councillor Profile</h2>
                {!editingProfile ? (
                  <button onClick={() => setEditingProfile(true)}
                    className="flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-bold font-mono text-xs px-4 py-2 transition-colors">
                    <Edit3 className="w-3.5 h-3.5" />EDIT PROFILE
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={() => { setEditingProfile(false); }}
                      className="flex items-center gap-2 border border-gray-700 text-gray-400 hover:text-white font-mono text-xs px-3 py-2 transition-colors">
                      <X className="w-3.5 h-3.5" />CANCEL
                    </button>
                    <button onClick={handleProfileSave} disabled={profileSaving}
                      className="flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] disabled:opacity-50 text-black font-bold font-mono text-xs px-4 py-2 transition-colors">
                      <Save className="w-3.5 h-3.5" />{profileSaving ? 'SAVING...' : 'SAVE'}
                    </button>
                  </div>
                )}
              </div>

              <div className="bg-[#111] border border-[#1a1a1a] rounded p-6 space-y-5">
                <div className="flex items-center gap-4">
                  <img
                    src={profileForm.image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(profileForm.name || 'C')}&background=1a1a1a&color=fff&size=128`}
                    alt={profileForm.name}
                    className="w-20 h-20 rounded object-cover border border-gray-700"
                    onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=C&background=1a1a1a&color=fff&size=128`; }}
                  />
                  {editingProfile && (
                    <div className="flex-1">
                      <label className="block font-mono text-xs text-gray-400 uppercase mb-1">Photo URL</label>
                      <input type="url" value={profileForm.image_url}
                        onChange={(e) => setProfileForm({ ...profileForm, image_url: e.target.value })}
                        className="w-full bg-[#0a0a0a] border border-[#2a2a2a] px-3 py-2 text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                        placeholder="https://city.regina.ca/councillor-photo.jpg" />
                    </div>
                  )}
                </div>

                {[{ key: 'name', label: 'Full Name', type: 'text', placeholder: 'Councillor Full Name' },
                  { key: 'email', label: 'City Email', type: 'email', placeholder: 'councillor@regina.ca' },
                  { key: 'phone', label: 'Phone', type: 'text', placeholder: '306-777-0000' }].map((field) => (
                  <div key={field.key}>
                    <label className="block font-mono text-xs text-gray-400 uppercase mb-1">{field.label}</label>
                    {editingProfile ? (
                      <input type={field.type} value={profileForm[field.key] || ''}
                        onChange={(e) => setProfileForm({ ...profileForm, [field.key]: e.target.value })}
                        className="w-full bg-[#0a0a0a] border border-[#2a2a2a] px-4 py-2 text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none"
                        placeholder={field.placeholder} />
                    ) : (
                      <p className="text-white font-mono text-sm">{profileForm[field.key] || <span className="text-gray-600 italic">Not set</span>}</p>
                    )}
                  </div>
                ))}

                <div>
                  <label className="block font-mono text-xs text-gray-400 uppercase mb-1">Bio / About</label>
                  {editingProfile ? (
                    <textarea rows={5} value={profileForm.bio}
                      onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a2a] px-4 py-3 text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none resize-none"
                      placeholder="Councillor background and priorities..." />
                  ) : (
                    <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                      {profileForm.bio || <span className="text-gray-600 italic">No bio added yet.</span>}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminCouncillorView;
